import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Phone, Clock, Heart, Calendar, ChevronDown, ChevronUp } from 'lucide-react';
import { useAnalytics } from '../../hooks/useAnalytics';
import { InfoIcon, ItineraryIcon, HotelItineraryIcon, GiftIcon, GoogleMapIcon } from '../icons';
import type { EventItem, Place } from '../../types/shared/event';
import { useSection } from '../../hooks/useSection';
import { useAutoTranslation } from '../../hooks/useAutoTranslation';
import { FavoriteButton } from './FavoriteButton';
import { favoritesService } from '../../lib/favorites/favoritesService';
import { logger } from '../../lib/logger';
import { useQuery } from '@tanstack/react-query';
import { useImageDisplay } from '../../hooks/useImageDisplay';
import { CalendarModal } from './CalendarModal';
import { navigationClient } from '../../lib/navigation';
import { formatOpeningHours } from '../../utils/openingHoursFormatter';
import { getDirectionsUrl } from '../../utils/itineraryUtils';
import type { SupportedLanguages } from '../../i18n';

const ActionButtonWrapper: React.FC<{ tooltip: string; children: React.ReactElement }> = ({ tooltip, children }) => {
  const [show, setShow] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {children}
      {show && tooltip && (
        <div
          className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5 px-2.5 py-1 text-[11px] font-medium text-white bg-gray-800 rounded-md whitespace-nowrap pointer-events-none z-50 shadow-lg"
          style={{ animation: 'tooltipFadeIn 0.1s ease-out' }}
        >
          {tooltip}
          <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-t-[5px] border-t-gray-800" />
        </div>
      )}
    </div>
  );
};

const CLOSE_ANIMATION_DURATION = 250;

const navigationVariants = {
  enter: (direction: number) => ({
    x: direction * window.innerWidth,
    opacity: 0
  }),
  center: {
    x: 0,
    opacity: 1
  },
  exit: (direction: number) => ({
    x: -direction * window.innerWidth,
    opacity: 0
  })
};

interface BaseDetailCardProps {
  item: EventItem;
  items: EventItem[];
  onClose: () => void;
  onItemChange: (item: EventItem) => void;
  config: any;
  translations: Record<string, Record<string, string>>;
  tableId: string;
  renderContent: (item: EventItem) => React.ReactNode;
  renderBookingButton?: (item: EventItem) => React.ReactNode;
  onCategorySelect: (category: string) => void;
  onTypeSelect: (type: string) => void;
  categoryColors: Map<string, string>;
  getTranslatedCategory: (category: string) => string;
  getTranslatedText: (key: string) => string;
  behavior?: 'default' | 'pin' | 'place';
  showIndex?: number | null;
  place?: Place | null;
  context?: {
    tableId?: string;
    sectionType?: string;
    itemId?: string;
    idShow?: string;
    show_id?: string;
    source_hash?: string;
    contentType?: 'description' | 'offer';
  };
  itineraryUrl?: string;
  googleMapsUrl?: string;
  otherDates?: EventItem[];
  onOtherDateClick?: (event: EventItem) => void;
  isFavoriteCard?: boolean;
  image?: string;
  description?: string;
  offer?: string;
  opening_hours?: string | null | any;
  translating?: boolean;
  translationError?: Error | null;
  onRemoveFavorite?: () => void;
  onShowPlace?: (place: Place) => void;
  isFavorite?: boolean;
  onFavoriteToggle?: (newState: boolean) => void;
}

export const BaseDetailCard: React.FC<BaseDetailCardProps> = ({
  item,
  items,
  onClose,
  onItemChange,
  config,
  translations,
  tableId,
  renderContent,
  renderBookingButton,
  onCategorySelect,
  onTypeSelect,
  categoryColors,
  getTranslatedCategory,
  getTranslatedText,
  behavior = 'default',
  showIndex,
  place,
  context,
  itineraryUrl,
  googleMapsUrl,
  otherDates,
  onOtherDateClick,
  isFavoriteCard = false,
  image,
  description,
  offer,
  opening_hours,
  translating,
  translationError,
  onRemoveFavorite,
  onShowPlace,
  isFavorite,
  onFavoriteToggle
}) => {
  const { i18n } = useTranslation();
  const section = useSection();
  const analytics = useAnalytics();
  const [direction, setDirection] = useState(0);
  const [openTime] = useState(Date.now());
  const [localIsFavorite, setLocalIsFavorite] = useState(() => {
    if (isFavorite !== undefined) return isFavorite;
    const favoriteId = item.source === 'ticketmaster' ? `ticketmaster_${item.eventId}` : item.id;
    return favoritesService.isFavorite(favoriteId);
  });
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [isOpeningHoursExpanded, setIsOpeningHoursExpanded] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const handleClose = useCallback(() => {
    if (isClosing) return;
    setIsClosing(true);
    setTimeout(() => onClose(), CLOSE_ANIMATION_DURATION);
  }, [isClosing, onClose]);

  // Keep track of the current item to prevent unwanted reloads
  const [currentItem, setCurrentItem] = useState(item);
  const previousItemRef = useRef(item.id);

  const { data: navigation } = useQuery({
    queryKey: ['navigation'],
    queryFn: () => navigationClient.getNavigation(),
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000
  });

  // Check if we should display the image
  const { shouldDisplay } = useImageDisplay(image, 'detailCard');

  // Save scroll position when the component mounts
  useEffect(() => {
    // Prevent body scrolling while modal is open
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = 'hidden';
    
    return () => {
      // Restore body scrolling when modal closes
      document.body.style.overflow = originalStyle;
    };
  }, [item.id, item.name, behavior, shouldDisplay, offer]);

  // Update currentItem when item changes
  useEffect(() => {
    if (item.id !== previousItemRef.current) {
      setCurrentItem(item);
      previousItemRef.current = item.id;
    }
  }, [item, offer]);

  // Trouver les événements du même spectacle pour le mode "pin"
  const sameShowEvents = React.useMemo(() => {
    if (behavior !== 'pin') return [];
    
    return items.filter(event => {
      if (event.source === 'airtable' && currentItem.source === 'airtable') {
        return (event as any).idShow === (currentItem as any).idShow || 
               (event as any).idshow === (currentItem as any).idshow ||
               (event as any).show_id === (currentItem as any).show_id;
      }
      if (event.source === 'ticketmaster' && currentItem.source === 'ticketmaster') {
        return (event as any).showId === (currentItem as any).showId;
      }
      return false;
    });
  }, [behavior, items, currentItem]);

  // Déterminer si on doit afficher la navigation
  const showNavigation = React.useMemo(() => {
    if (behavior === 'place') {
      return place && place.shows.length > 1;
    }
    if (behavior === 'pin') {
      return sameShowEvents.length > 1;
    }
    return items.length > 1;
  }, [behavior, place, items, sameShowEvents]);

  const translationContext = {
    tableId: context?.tableId || tableId,
    itemId: currentItem.id,
    sectionType: context?.sectionType || currentItem.source || section.type_rubrique,
    idShow: context?.idShow || currentItem.idShow || (currentItem as any).idshow,
    show_id: context?.show_id || currentItem.show_id,
    contentType: context?.contentType || 'description'
  };

  useEffect(() => {
    return () => {
      const duration = (Date.now() - openTime) / 1000;
      analytics.trackEvent('content_view', 'detail_card_view', {
        section_id: section.id,
        item_id: currentItem.id,
        item_type: currentItem.type || 'standard',
        duration: duration,
        item_name: currentItem.name,
        category: currentItem.category,
        type: currentItem.type,
        section_name: section.names.fr || 'Unknown Section',
        section_type: section.type_rubrique || 'unknown'
      });
    };
  }, [analytics, currentItem.id, openTime, section.id, currentItem.type, currentItem.name, currentItem.category, section.names.fr, section.type_rubrique]);

  useEffect(() => {
    // Update local favorite state when prop changes
    if (isFavorite !== undefined) {
      setLocalIsFavorite(isFavorite);
    }
  }, [isFavorite]);

  const trackAction = (action: string, additionalData?: Record<string, any>) => {
    analytics.trackEvent('interaction', action, {
      section_id: section.id,
      item_id: currentItem.id,
      item_type: currentItem.type || 'standard',
      item_name: currentItem.name,
      category: currentItem.category,
      type: currentItem.type,
      section_name: section.names.fr || 'Unknown Section',
      section_type: section.type_rubrique || 'unknown',
      ...additionalData
    });
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handlePrevItem = () => {
    if (!items || items.length === 0) return;
    setDirection(-1);
    setIsOpeningHoursExpanded(false);

    let nextItem;
    if (behavior === 'place' && place && showIndex !== null) {
      // Navigation entre les shows du même lieu
      const shows = place.shows;
      const prevIndex = showIndex === 0 ? shows.length - 1 : showIndex - 1;
      nextItem = shows[prevIndex].events[0]; // Prendre le premier événement du show précédent
    } else if (behavior === 'pin' && sameShowEvents.length > 1) {
      // Navigation entre les événements du même spectacle
      const currentIndex = sameShowEvents.findIndex(e => e.id === currentItem.id);
      const prevIndex = currentIndex === 0 ? sameShowEvents.length - 1 : currentIndex - 1;
      nextItem = sameShowEvents[prevIndex];
    } else {
      const currentIndex = items.findIndex(i => i.id === currentItem.id);
      const prevIndex = currentIndex === 0 ? items.length - 1 : currentIndex - 1;
      nextItem = items[prevIndex];
    }

    trackAction('navigation_prev', {
      nextItemId: nextItem.id,
      nextItemName: nextItem.name
    });
    setCurrentItem(nextItem);
    const nextFavoriteId = nextItem.source === 'ticketmaster' ? `ticketmaster_${nextItem.eventId}` : nextItem.id;
    setLocalIsFavorite(favoritesService.isFavorite(nextFavoriteId));
    onItemChange(nextItem);
  };

  const handleNextItem = () => {
    if (!items || items.length === 0) return;
    setDirection(1);
    setIsOpeningHoursExpanded(false);

    let nextItem;
    if (behavior === 'place' && place && showIndex !== null) {
      // Navigation entre les shows du même lieu
      const shows = place.shows;
      const nextIndex = showIndex === shows.length - 1 ? 0 : showIndex + 1;
      nextItem = shows[nextIndex].events[0]; // Prendre le premier événement du show suivant
    } else if (behavior === 'pin' && sameShowEvents.length > 1) {
      // Navigation entre les événements du même spectacle
      const currentIndex = sameShowEvents.findIndex(e => e.id === currentItem.id);
      const nextIndex = currentIndex === sameShowEvents.length - 1 ? 0 : currentIndex + 1;
      nextItem = sameShowEvents[nextIndex];
    } else {
      const currentIndex = items.findIndex(i => i.id === currentItem.id);
      const nextIndex = currentIndex === items.length - 1 ? 0 : currentIndex + 1;
      nextItem = items[nextIndex];
    }

    trackAction('navigation_next', {
      nextItemId: nextItem.id,
      nextItemName: nextItem.name
    });
    setCurrentItem(nextItem);
    const nextFavoriteId = nextItem.source === 'ticketmaster' ? `ticketmaster_${nextItem.eventId}` : nextItem.id;
    setLocalIsFavorite(favoritesService.isFavorite(nextFavoriteId));
    onItemChange(nextItem);
  };

  const handleFavoriteToggle = async (newState: boolean) => {
    try {
      const currentSection = section;
      
      if (!currentSection) {
        logger.error('No current section found', {
          category: 'Favorites',
          data: { section }
        });
        return;
      }

      if (onFavoriteToggle) {
        onFavoriteToggle(newState);
      } else {
        if (newState) {
          favoritesService.addFavorite({
            id: currentItem.source === 'ticketmaster' ? `ticketmaster_${currentItem.eventId}` : currentItem.id,
            sourceType: currentSection.type_rubrique,
            data: currentItem,
            metadata: {
              sourceSection: section,
              sectionSlug: navigationClient.generateSlug(currentSection.names.fr),
              sectionNames: currentSection.names
            }
          });
          
          trackAction('add_favorite', {
            item_name: currentItem.name,
            category: currentItem.category,
            type: currentItem.type
          });
        } else {
          favoritesService.removeFavorite(currentItem.source === 'ticketmaster' ? `ticketmaster_${currentItem.eventId}` : currentItem.id);
          
          trackAction('remove_favorite', {
            item_name: currentItem.name,
            category: currentItem.category,
            type: currentItem.type
          });
        }
        
        setLocalIsFavorite(newState);
      }
    } catch (error) {
      logger.error('Error toggling favorite', {
        category: 'Favorites',
        data: { error, itemId: currentItem.id }
      });
    }
  };

  const handleCalendarClick = () => {
    // Safely handle potentially undefined description
    const eventDescription = description || currentItem.description || '';
    
    // Log if both description sources are missing
    if (!description && !currentItem.description) {
      logger.warn('Both description prop and item.description are undefined', {
        category: 'Calendar',
        data: {
          itemId: currentItem.id,
          itemName: currentItem.name,
          itemType: currentItem.type || 'standard'
        }
      });
    }
    
    trackAction('calendar_click', {
      item_name: currentItem.name,
      category: currentItem.category,
      type: currentItem.type
    });
    
    setShowCalendarModal(true);
  };

  // Render the alternative header when image is not displayed
  const renderAlternativeHeader = () => (
    <div className="relative h-16 flex items-center justify-between px-4">
      {/* Favorite button on the left */}
      {!isFavoriteCard ? (
        <button
          onClick={() => handleFavoriteToggle(!localIsFavorite)}
          className="absolute top-0 left-0 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center transition-opacity hover:opacity-80 z-10"
          style={{ 
            backgroundColor: config['major-color'],
            borderTopLeftRadius: config.border_radius,
            borderBottomRightRadius: config.border_radius
          }}
          aria-label={localIsFavorite ? 'Retirer des favoris' : 'Ajouter aux favoris'}
        >
          <Heart
            size={24}
            className="text-white"
            fill={localIsFavorite ? 'currentColor' : 'none'}
          />
        </button>
      ) : onRemoveFavorite ? (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemoveFavorite();
            handleClose();
          }}
          className="absolute top-0 left-0 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center transition-opacity hover:opacity-80 z-10"
          style={{
            backgroundColor: config['major-color'],
            borderTopLeftRadius: config.border_radius,
            borderBottomRightRadius: config.border_radius
          }}
          aria-label="Retirer des favoris"
        >
          <Heart
            size={24}
            className="text-white"
            fill="currentColor"
          />
        </button>
      ) : null}

      {/* Close button on the right */}
      <button
        onClick={handleClose}
        className="absolute top-0 right-0 w-10 h-10 sm:w-12 sm:h-12 bg-black bg-opacity-50 text-white hover:bg-opacity-70 transition-opacity flex items-center justify-center"
        style={{ 
          borderTopRightRadius: config.border_radius,
          borderBottomLeftRadius: config.border_radius
        }}
      >
        <X size={20} />
      </button>

      {/* Navigation buttons in the center */}
      {showNavigation && (
        <div className="flex justify-center items-center w-full">
          <button
            onClick={handlePrevItem}
            className="w-10 h-10 flex items-center justify-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ChevronLeft size={24} />
          </button>
          <div className="mx-2 text-sm text-gray-500">
            {items.findIndex(i => i.id === currentItem.id) + 1} / {items.length}
          </div>
          <button
            onClick={handleNextItem}
            className="w-10 h-10 flex items-center justify-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ChevronRight size={24} />
          </button>
        </div>
      )}
    </div>
  );

  // Check if the calendar icon should be shown
  // Only show for event and event-api sections
  const showCalendarIcon = context?.sectionType === 'event' || 
                          context?.sectionType === 'event-api' || 
                          section.type_rubrique === 'event' || 
                          section.type_rubrique === 'event-api';

  const actionButtons = React.useMemo(() => {
    const buttons: React.ReactNode[] = [];

    if (currentItem.url) {
      buttons.push(
        <ActionButtonWrapper key="info" tooltip={getTranslatedText('btn_more_info')}>
          <a
            href={currentItem.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackAction('more_info_click', { url: currentItem.url, item_name: currentItem.name })}
            className="flex flex-col items-center justify-center p-2 sm:p-3 rounded-lg hover:bg-gray-50 transition-colors"
            style={{ color: config['medium-color'] }}
          >
            <InfoIcon size={20} />
          </a>
        </ActionButtonWrapper>
      );
    }

    if (currentItem.telephone) {
      buttons.push(
        <ActionButtonWrapper key="phone" tooltip={getTranslatedText('btn_call')}>
          <a
            href={`tel:${currentItem.telephone}`}
            onClick={() => trackAction('call_click', { phone: currentItem.telephone, item_name: currentItem.name })}
            className="flex flex-col items-center justify-center p-2 sm:p-3 rounded-lg hover:bg-gray-50 transition-colors"
            style={{ color: config['medium-color'] }}
          >
            <Phone size={20} />
          </a>
        </ActionButtonWrapper>
      );
    }

    if (googleMapsUrl) {
      buttons.push(
        <ActionButtonWrapper key="gmaps" tooltip={getTranslatedText('btn_google_maps')}>
          <a
            href={googleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackAction('google_maps_click', { url: googleMapsUrl, item_name: currentItem.name })}
            className="flex flex-col items-center justify-center p-2 sm:p-3 rounded-lg hover:bg-gray-50 transition-colors"
            style={{ color: config['medium-color'] }}
          >
            <GoogleMapIcon size={20} />
          </a>
        </ActionButtonWrapper>
      );
    }

    if (currentItem.fulladress || (currentItem as any).google_map) {
      buttons.push(
        <ActionButtonWrapper key="directions" tooltip={getTranslatedText('btn_itinerary')}>
          <a
            href={getDirectionsUrl(currentItem.fulladress || '', (currentItem as any).google_map || undefined)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackAction('directions_click', { address: currentItem.fulladress, item_name: currentItem.name })}
            className="flex flex-col items-center justify-center p-2 sm:p-3 rounded-lg hover:bg-gray-50 transition-colors"
            style={{ color: config['medium-color'] }}
          >
            <ItineraryIcon size={20} />
          </a>
        </ActionButtonWrapper>
      );
    }

    if (itineraryUrl) {
      buttons.push(
        <ActionButtonWrapper key="hotel-directions" tooltip={getTranslatedText('btn_itinerary_hotel')}>
          <a
            href={itineraryUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackAction('hotel_directions_click', { url: itineraryUrl, item_name: currentItem.name })}
            className="flex flex-col items-center justify-center p-2 sm:p-3 rounded-lg hover:bg-gray-50 transition-colors"
            style={{ color: config['medium-color'] }}
          >
            <HotelItineraryIcon size={20} />
          </a>
        </ActionButtonWrapper>
      );
    }

    if (showCalendarIcon) {
      buttons.push(
        <ActionButtonWrapper key="calendar" tooltip={getTranslatedText('add_to_calendar') || 'Add to Calendar'}>
          <button
            onClick={handleCalendarClick}
            className="flex flex-col items-center justify-center p-2 sm:p-3 rounded-lg hover:bg-gray-50 transition-colors"
            style={{ color: config['medium-color'] }}
          >
            <Calendar size={20} />
          </button>
        </ActionButtonWrapper>
      );
    }

    return buttons;
  }, [currentItem, googleMapsUrl, itineraryUrl, showCalendarIcon, config, getTranslatedText, trackAction, handleCalendarClick]);

  return (
    <motion.div
      className="modal-container detail-card-modal"
      onClick={handleOverlayClick}
      initial={{ opacity: 0 }}
      animate={{ opacity: isClosing ? 0 : 1 }}
      transition={{ duration: CLOSE_ANIMATION_DURATION / 1000 }}
    >
      <motion.div
        className="w-full max-w-2xl"
        initial={{ y: '40%', opacity: 0 }}
        animate={{
          y: isClosing ? '-15%' : 0,
          opacity: isClosing ? 0 : 1
        }}
        transition={{
          type: 'spring',
          damping: 28,
          stiffness: 350,
          mass: 0.8,
          opacity: { duration: CLOSE_ANIMATION_DURATION / 1000 }
        }}
        onClick={e => e.stopPropagation()}
      >
        <AnimatePresence initial={false} mode="wait" custom={direction}>
          <motion.div
            key={currentItem.id}
            className="bg-white w-full relative flex flex-col overflow-hidden"
            style={{
              borderRadius: config.border_radius,
              maxHeight: 'calc(100dvh - var(--detail-card-padding-top, 1rem) - var(--detail-card-padding-bottom, 1rem))'
            }}
            custom={direction}
            variants={navigationVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: "tween", duration: 0.2, ease: "easeInOut" },
              opacity: { duration: 0.2 }
            }}
          >
          {shouldDisplay && image ? (
            <div className="shrink-0">
              <div className="relative">
                <div className="w-full relative overflow-hidden bg-gray-100 md:pb-[50%] pb-[56.25%]">
                  <img
                    src={image}
                    alt={currentItem.name}
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="eager"
                  />
                </div>

                {!isFavoriteCard && (
                  <FavoriteButton
                    itemId={currentItem.source === 'ticketmaster' ? `ticketmaster_${currentItem.eventId}` : currentItem.id}
                    isFavorite={localIsFavorite}
                    onToggle={handleFavoriteToggle}
                    config={config}
                    translations={translations}
                  />
                )}

                {isFavoriteCard && onRemoveFavorite && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveFavorite();
                      handleClose();
                    }}
                    className="absolute top-0 left-0 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center transition-opacity hover:opacity-80 z-10"
                    style={{
                      backgroundColor: config['major-color'],
                      borderTopLeftRadius: config.border_radius,
                      borderBottomRightRadius: config.border_radius
                    }}
                    aria-label="Retirer des favoris"
                  >
                    <Heart
                      size={24}
                      className="text-white"
                      fill="currentColor"
                    />
                  </button>
                )}

                <button
                  onClick={handleClose}
                  className="absolute top-0 right-0 w-10 h-10 sm:w-12 sm:h-12 bg-black bg-opacity-50 text-white hover:bg-opacity-70 transition-opacity flex items-center justify-center"
                  style={{
                    borderTopRightRadius: config.border_radius,
                    borderBottomLeftRadius: config.border_radius
                  }}
                >
                  <X size={20} />
                </button>

                {showNavigation && (
                  <>
                    <button
                      onClick={handlePrevItem}
                      className="absolute left-0 bottom-0 w-10 h-10 sm:w-12 sm:h-12 bg-black bg-opacity-50 text-white hover:bg-opacity-70 transition-opacity flex items-center justify-center"
                      style={{ borderTopRightRadius: config.border_radius }}
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <button
                      onClick={handleNextItem}
                      className="absolute right-0 bottom-0 w-10 h-10 sm:w-12 sm:h-12 bg-black bg-opacity-50 text-white hover:bg-opacity-70 transition-opacity flex items-center justify-center"
                      style={{ borderTopLeftRadius: config.border_radius }}
                    >
                      <ChevronRight size={20} />
                    </button>
                  </>
                )}
              </div>

              {actionButtons.length > 0 && (
                <div className="border-b border-gray-200">
                  <div
                    className="flex items-center justify-center gap-1 px-4 py-0 bg-gray-50/80"
                  >
                    {actionButtons}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <>
              {renderAlternativeHeader()}
              {actionButtons.length > 0 && (
                <div className="border-b border-gray-200">
                  <div
                    className="flex items-center justify-center gap-1 px-4 py-0 bg-gray-50/80"
                  >
                    {actionButtons}
                  </div>
                </div>
              )}
            </>
          )}

          <div className="shrink-0 px-4 sm:px-6 pt-3">
            {currentItem.booking && renderBookingButton && (
              <div onClick={() => {
                trackAction('booking_click', {
                  item_name: currentItem.name,
                  url: currentItem.booking
                });
              }}>
                {renderBookingButton(currentItem)}
              </div>
            )}

            <h2 className="text-xl sm:text-2xl font-bold mb-1">{currentItem.name}</h2>
          </div>

          <div className="overflow-y-auto flex-1 min-h-0 scrollbar-hide">
            <div className="px-4 sm:px-6 pb-4 sm:pb-6">
              {renderContent(currentItem)}

              <div className="flex flex-wrap gap-1.5 mb-4">
                <button
                  type="button"
                  onClick={() => {
                    trackAction('category_filter', {
                      category: currentItem.category,
                      item_name: currentItem.name
                    });
                    onCategorySelect(currentItem.category);
                  }}
                  className="inline-block px-1.5 py-0.5 text-xs rounded hover:opacity-80 transition-opacity text-white"
                  style={{ backgroundColor: categoryColors.get(currentItem.category) || config['medium-color'] }}
                >
                  {getTranslatedCategory(currentItem.category)}
                </button>

                {currentItem.type && (
                  <button
                    type="button"
                    onClick={() => {
                      trackAction('type_filter', {
                        type: currentItem.type,
                        item_name: currentItem.name
                      });
                      onTypeSelect(currentItem.type);
                    }}
                    className="inline-block px-1.5 py-0.5 text-xs rounded hover:opacity-80 transition-opacity text-white opacity-70"
                    style={{ backgroundColor: config['medium-color'] }}
                  >
                    {getTranslatedCategory(currentItem.type)}
                  </button>
                )}
              </div>

              <div className="relative mb-4">
                {translating && (
                  <div
                    className="absolute -top-6 right-0 px-2 py-1 rounded text-white text-sm"
                    style={{ backgroundColor: config['medium-color'] }}
                  >
                    {getTranslatedText('translating')}
                  </div>
                )}

                {translationError && (
                  <div
                    className="absolute -top-6 right-0 px-2 py-1 rounded text-white text-sm"
                    style={{ backgroundColor: config['major-color'] }}
                  >
                    {getTranslatedText('translation_failed')}
                  </div>
                )}

                <div
                  className="text-gray-700 text-sm sm:text-base"
                  dangerouslySetInnerHTML={{ __html: description || '' }}
                />

                {opening_hours && (
                  <div className="mt-4 bg-gray-50 rounded-lg border border-gray-200">
                    <button
                      onClick={() => setIsOpeningHoursExpanded(!isOpeningHoursExpanded)}
                      className="w-full p-4 flex items-center justify-between hover:bg-gray-100 transition-colors rounded-lg"
                    >
                      <div className="flex items-center gap-2">
                        <Clock size={20} style={{ color: config['major-color'] }} />
                        <h3 className="text-base font-semibold" style={{ color: config['major-color'] }}>
                          {getTranslatedText('opening_hours') || 'Horaires d\'ouverture'}
                        </h3>
                      </div>
                      {isOpeningHoursExpanded ? (
                        <ChevronUp size={20} style={{ color: config['major-color'] }} />
                      ) : (
                        <ChevronDown size={20} style={{ color: config['major-color'] }} />
                      )}
                    </button>
                    {isOpeningHoursExpanded && (
                      <div
                        className="px-4 pb-4 text-gray-700 text-sm"
                        dangerouslySetInnerHTML={{
                          __html: formatOpeningHours(opening_hours, i18n.language as SupportedLanguages)
                        }}
                      />
                    )}
                  </div>
                )}

                {offer && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200 relative">
                    <div className="flex items-center gap-2 mb-3">
                      <div
                        className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded-full"
                        style={{ backgroundColor: config['major-color'] }}
                      >
                        <GiftIcon size={16} className="text-white" />
                      </div>
                      <h3 className="text-base sm:text-lg font-semibold" style={{ color: config['major-color'] }}>
                        {getTranslatedText('special_offer')}
                      </h3>
                    </div>
                    <div
                      className="text-gray-700"
                      dangerouslySetInnerHTML={{ __html: offer }}
                    />
                  </div>
                )}
              </div>

              {!isFavoriteCard && otherDates && otherDates.length > 0 && onOtherDateClick && (
                <div className="mb-4 sm:mb-6">
                  <h3 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3">
                    {getTranslatedText('other_dates')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {otherDates.map(event => (
                      <button
                        key={event.id}
                        onClick={() => {
                          trackAction('other_date_click', {
                            dateId: event.id,
                            dateName: event.name,
                            date: event.date,
                            hour: event.hour
                          });
                          onOtherDateClick(event);
                        }}
                        className="flex items-center justify-between px-3 py-2 bg-white rounded-lg hover:bg-gray-50 transition-colors text-left border border-gray-100"
                      >
                        <div className="flex items-center gap-2 truncate">
                          <Clock size={16} className="shrink-0 text-gray-500" />
                          <span className="truncate text-sm">
                            {new Intl.DateTimeFormat(i18n.language, {
                              weekday: 'short',
                              day: 'numeric',
                              month: 'short',
                              hour: 'numeric',
                              minute: 'numeric'
                            }).format(new Date(`${event.date}T${event.hour}`))}
                          </span>
                        </div>
                        {event.price && (
                          <span
                            className="text-xs md:text-sm font-medium px-2 py-0.5 rounded-full shrink-0 ml-2"
                            style={{ color: config['major-color'] }}
                          >
                            {event.price}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {renderBookingButton && !currentItem.booking && renderBookingButton(currentItem)}
            </div>
          </div>
          </motion.div>
        </AnimatePresence>
      </motion.div>

      {showCalendarModal && (
        <CalendarModal
          isOpen={showCalendarModal}
          onClose={() => setShowCalendarModal(false)}
          event={currentItem}
          config={config}
          translations={translations}
          description={description || currentItem.description || ''}
        />
      )}
    </motion.div>
  );
};

export default BaseDetailCard;